// A small body's real shape on a SphereGeometry: each vertex moved out or in
// to the body's radius in its own direction, read from a radius grid such as
// src/data/phobosShape.js (scripts/build-phobos.mjs). The mean radius stays
// the radius the sphere was built with, so the body's size is unchanged.
//
// A vertex's latitude and longitude are read from its UV, which is how the
// body's map is laid on the same sphere: u = 0 is longitude -180 and u = 0.5
// the prime meridian, v = 1 the north pole. Shape and map cannot drift apart.

const decoded = new WeakMap();

function radiiOf(shape) {
  let radii = decoded.get(shape);
  if (!radii) {
    const bytes = atob(shape.data);
    radii = new Float32Array(bytes.length);
    const span = shape.max - shape.min;
    for (let i = 0; i < bytes.length; i++) radii[i] = shape.min + (bytes.charCodeAt(i) / 255) * span;
    decoded.set(shape, radii);
  }
  return radii;
}

// The radius as a fraction of the mean at (u, v) of the body's map,
// bilinear, wrapping in longitude.
export function shapeRadiusAt(shape, u, v) {
  const radii = radiiOf(shape);
  const { cols, rows } = shape;
  const fx = (((u % 1) + 1) % 1) * cols;
  const fy = Math.min(rows - 1, Math.max(0, (1 - v) * (rows - 1)));
  const x0 = Math.floor(fx) % cols;
  const x1 = (x0 + 1) % cols;
  const y0 = Math.min(rows - 2, Math.floor(fy));
  const tx = fx - Math.floor(fx);
  const ty = fy - y0;
  const at = (x, y) => radii[y * cols + x];
  return (at(x0, y0) * (1 - tx) + at(x1, y0) * tx) * (1 - ty)
    + (at(x0, y0 + 1) * (1 - tx) + at(x1, y0 + 1) * tx) * ty;
}

// Reshape the sphere in place. Normals are recomputed and then shared by
// vertices that sit at one point (the seam's two copies, a pole's fan), so
// the light runs across the seam without a crease.
export function applyShapeModel(geometry, shape) {
  const pos = geometry.attributes.position;
  const uv = geometry.attributes.uv;
  for (let i = 0; i < pos.count; i++) {
    const s = shapeRadiusAt(shape, uv.getX(i), uv.getY(i));
    pos.setXYZ(i, pos.getX(i) * s, pos.getY(i) * s, pos.getZ(i) * s);
  }
  pos.needsUpdate = true;
  geometry.computeVertexNormals();

  const normal = geometry.attributes.normal;
  const groups = new Map();
  // Rounded to whole numbers, not toFixed: sin(2 pi) is -2e-16, and toFixed
  // writes the seam's far copy as "-0.00000", so the seam and the south pole
  // kept their creases.
  const keyOf = (i) => `${Math.round(pos.getX(i) * 1e5)},${Math.round(pos.getY(i) * 1e5)},${Math.round(pos.getZ(i) * 1e5)}`;
  for (let i = 0; i < pos.count; i++) {
    const key = keyOf(i);
    const g = groups.get(key);
    if (g) g.push(i); else groups.set(key, [i]);
  }
  for (const members of groups.values()) {
    if (members.length < 2) continue;
    let x = 0, y = 0, z = 0;
    for (const i of members) { x += normal.getX(i); y += normal.getY(i); z += normal.getZ(i); }
    const len = Math.hypot(x, y, z) || 1;
    for (const i of members) normal.setXYZ(i, x / len, y / len, z / len);
  }
  normal.needsUpdate = true;
  geometry.computeBoundingSphere();
  geometry.computeBoundingBox();
  return geometry;
}
